import Head from "next/head"
import dynamic from "next/dynamic"
import Image from "next/image"
import ReactPlayer from "react-player"
import Link from "next/link"
import { Breadcrumb, Col, Container, Row } from "react-bootstrap"
import { GetStaticPaths, GetStaticProps, NextPage } from "next"
import { initializeApollo } from "apollo/client"
import Header from "@modules/Common/components/Header"
import Page from "@modules/Common/components/ClientPage"
import { DEFAULT_META_IMAGE_URL } from "@utils/constant"
import WebFooter from "@modules/Common/components/WebFooter"
import { useDesktopMediaQuery } from "@modules/Common/components/Device"
import { PublicWorkshopDocument, PublicWorkshopQuery, PublicWorkshopQueryVariables } from "@generated/graphql"


const WorkshopDetail = dynamic(() => import("@modules/Workshop/components/WorkshopDetail"), {
  ssr: false
})

interface IWorkshopPage {
  workshop?: PublicWorkshopQuery["publicWorkshop"]
}

const WorkshopPage: NextPage<IWorkshopPage> = ({ workshop }) => {

  const title = `${workshop?.metaTagData?.title || workshop?.name || ""} | MySkillShaala`
  const description = workshop?.metaTagData?.description || workshop?.description || ""
  const image = workshop?.metaTagData?.image || workshop?.image || DEFAULT_META_IMAGE_URL
  const isDesktopOrLaptop = useDesktopMediaQuery()

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/workshops/${workshop?.slug}`} />
        <meta property="og:site_name" content={process.env.NEXT_PUBLIC_SITE_URL} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/workshops/${workshop?.slug}`} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={image} />
        {workshop && <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify([{
            "@context": "https://schema.org",
            "@type": "Course",
            "name": workshop.name,
            "description": workshop.description,
            "provider": {
              "@type": "Organization",
              "name": "MySkillShaala",
              "sameAs": "https://myskillshaala.com"
            }
          }, {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "Workshops",
              "item": `${process.env.NEXT_PUBLIC_SITE_URL}/workshops`
            }, {
              "@type": "ListItem",
              "position": 2,
              "name": workshop.name,
              "item": `${process.env.NEXT_PUBLIC_SITE_URL}/workshops/${workshop.slug}`
            }]
          }])
        }} />}
      </Head>
      <Page>
        <Header />
        <Breadcrumb className="ms-5">
          <Breadcrumb.Item linkAs={Link} href="/dashboard">Home</Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} href="/workshops">Workshops</Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} href={`/workshops/${workshop?.slug}`}>{workshop?.name}</Breadcrumb.Item>
        </Breadcrumb>
        {isDesktopOrLaptop ? <>
          {workshop?.headerImageLarge && <>
            <Image src={workshop?.headerImageLarge} className="workshop-header-img" sizes="100vw" fill alt="Header Image" />
            {workshop.headerVideo && <div className="workshop-header-video">
              <ReactPlayer
                url={workshop.headerVideo}
                height={300}
                config={{ vimeo: { playerOptions: { controls: true } } }}
              />
              {workshop.headerText && <div className="workshop-header-text" dangerouslySetInnerHTML={{ __html: workshop.headerText || "" }} />}
            </div>}
          </>}
          {workshop && <WorkshopDetail workshop={workshop} />}
          {workshop?.footerImageLarge && <>
            <Image src={workshop?.footerImageLarge} className="workshop-footer-img" sizes="100vw" fill alt="Footer Image" />
          </>}
        </>
          : <>{workshop?.headerImageSmall && <>
            <Image src={workshop?.headerImageSmall} className="workshop-header-img" sizes="100vw" fill alt="Header Image" />
            {workshop.headerVideo && <div className="workshop-header-video">
              <ReactPlayer
                url={workshop.headerVideo}
                width="100%"
                height={200}
                config={{ vimeo: { playerOptions: { controls: true } } }}
              />
              {workshop.headerText && <div className="workshop-header-text" dangerouslySetInnerHTML={{ __html: workshop.headerText || "" }} />}
            </div>}
          </>}
            {workshop && <WorkshopDetail workshop={workshop} />}
            {workshop?.footerImageSmall && <>
              <Image src={workshop?.footerImageSmall} className="workshop-footer-img" sizes="100vw" fill alt="Footer Image" />
            </>}
          </>}
        <WebFooter classes={workshop?.footerImageLarge ? "workshop-footer" : ""} />
      </Page>
    </>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return { paths: [], fallback: "blocking" }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = Array.isArray(params?.slug) ? params?.slug[0] : params?.slug!!
  const apolloClient = initializeApollo()
  const { data } = await apolloClient.query<PublicWorkshopQuery, PublicWorkshopQueryVariables>({
    query: PublicWorkshopDocument,
    variables: {
      where: {
        slug: slug!!
      }
    }
  })
  return {
    props: {
      workshop: data.publicWorkshop,
      initialApolloState: apolloClient.cache.extract(),
    },
    revalidate: 5
  }
}

export default WorkshopPage
